import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ShowRow from './ShowRow';
import { searchShows } from '../utils/showSearch';
import SearchEmptyState from './SearchEmptyState';
import SearchResultsLoading from './SearchResultsLoading';

const ApplePodcastsSearch = ({ query, limit = 10, emptyMessage = 'No shows found.' }) => {
    const searchQuery = (query || '').trim();
    const [retry, setRetry] = useState(0);
    const [result, setResult] = useState(null);
    const authenticated = useSelector(state => state.user.authenticated);
    const uid = useSelector(state => state.user.uid);
    const requestKey = JSON.stringify([searchQuery, limit, retry, uid]);

    useEffect(() => {
        if (!searchQuery) return;

        const controller = new AbortController();
        let current = true;
        setResult({ requestKey, status: 'loading' });

        const fetchPodcasts = async () => {
            try {
                if (authenticated) {
                    const podcasts = await searchShows(searchQuery, controller.signal);
                    if (current) setResult({ requestKey, status: 'ready', podcasts });
                    return;
                }
                const encodedQuery = encodeURIComponent(searchQuery);
                const response = await fetch(
                    `https://itunes.apple.com/search?term=${encodedQuery}&entity=podcast&limit=${limit}`,
                    { signal: controller.signal },
                );
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                if (!Array.isArray(data?.results)) {
                    throw new Error('Invalid show search response');
                }

                const collectionIds = new Set();
                const feedURLs = new Set();
                const podcasts = data.results.filter(podcast => {
                    if (!podcast?.collectionName || !podcast.artworkUrl100 || !podcast.feedUrl ||
                        (podcast.collectionId && collectionIds.has(String(podcast.collectionId))) || feedURLs.has(podcast.feedUrl)) {
                        return false;
                    }
                    if (podcast.collectionId) collectionIds.add(String(podcast.collectionId));
                    feedURLs.add(podcast.feedUrl);
                    return true;
                }).map(podcast => ({
                    key: podcast.collectionId ? `apple:${podcast.collectionId}` : `rss:${podcast.feedUrl}`,
                    name: podcast.collectionName, creator: podcast.artistName,
                    imageURL: podcast.artworkUrl100, rssURL: podcast.feedUrl,
                    appleCollectionId: podcast.collectionId ? String(podcast.collectionId) : undefined,
                }));

                if (current) setResult({ requestKey, status: 'ready', podcasts });
            } catch {
                if (current) setResult({ requestKey, status: 'error' });
            }
        };

        fetchPodcasts();
        return () => {
            current = false;
            controller.abort();
        };
    }, [searchQuery, limit, requestKey, authenticated]);

    if (!searchQuery) return null;
    if (result?.requestKey !== requestKey || result.status === 'loading') {
        return <SearchResultsLoading kind='shows' />;
    }

    if (result.status === 'error') {
        return (
            <div role='alert'>
                <SearchEmptyState message="Couldn't load shows. Please try again." />
                <div style={{ textAlign: 'center', paddingBottom: '20px' }}>
                    <button type='button' className='clampSafeButton' onClick={() => setRetry(retry + 1)}>
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    if (result.podcasts.length === 0) {
        return <SearchEmptyState message={emptyMessage} />;
    }

    return (
        <div>
            <div style={{ padding: '10px 20px' }}>
                <div>
                    {result.podcasts.slice(0, limit).map(podcast => (
                        <ShowRow key={podcast.key} show={podcast} />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ApplePodcastsSearch;
